'use client';

import type { Variant } from 'motion/react';
import { AnimatePresence, domAnimation, LazyMotion } from 'motion/react';
import * as m from 'motion/react-m';
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';

import { MENU_LINK_CV, MENU_LINK_LCV } from '@/app/contents/top-navigation';
import type { NavItemType } from '@/app/types/top-navigation';
import type { VehicleType } from '@/app/types/vehicle-type';
import { MobileMenuNav } from '@/components/layouts/top-navigation/mobile/menu/nav';
import { Item } from '@/components/layouts/top-navigation/shared/menu-item/base/list/item';
import { Box } from '@/components/shared/layout/box';
import { css } from '@/stitches.config';

const MobileSubMenu = dynamic(
  () =>
    import('@/components/layouts/top-navigation/mobile/sub-menu').then(
      (mod) => mod.MobileSubMenu
    ),
  {
    ssr: false,
  }
);

const MenuCss = css({
  position: 'fixed',
  inset: 0,
  zIndex: '$header',
  backgroundColor: '$white',
});

const animateVariants: { [key: string]: Variant } = {
  slideIn: { x: '0%' },
  slideOutActiveItem: { x: '-100%' },
  slideOut: { x: '100%' },
};
const transition = { duration: 0.5, ease: [0.25, 0.74, 0.22, 0.99] };

interface MobileMenuProps {
  Logo: ReactNode;
  isOpen: boolean;
  activeItem?: NavItemType;
  items: NavItemType[];
  vehicleType: VehicleType;
  onSetActiveItem: (item?: NavItemType) => void;
  onClose: () => void;
}

export const MobileMenu = ({
  Logo,
  isOpen = false,
  activeItem,
  items = [],
  vehicleType,
  onSetActiveItem,
  onClose,
}: MobileMenuProps) => {
  const vehicleRedirect = vehicleType === 'lcv' ? MENU_LINK_CV : MENU_LINK_LCV;
  const onSelectNavItem = (item: NavItemType) => {
    typeof onSetActiveItem === 'function' && onSetActiveItem(item);
  };
  const clearSelectItem = () => {
    typeof onSetActiveItem === 'function' && onSetActiveItem(undefined);
  };
  const onCloseHandler = () => {
    clearSelectItem();
    typeof onClose === 'function' && onClose();
  };

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        {!!isOpen && (
          <m.aside
            variants={animateVariants}
            initial="slideOut"
            animate={!!activeItem ? 'slideOutActiveItem' : 'slideIn'}
            exit="slideOut"
            transition={transition}
            className={MenuCss()}
          >
            <Box
              role="navigation"
              aria-label="Navigation menu"
              display="flex"
              direction="column"
              gap={{ '@initial': 4, '@xl': 0 }}
              position="absolute"
              inset="0"
              paddingTop="14"
            >
              <Box
                display="flex"
                direction="column"
                rowGap="4"
                overflow="auto"
                paddingY={{ '@initial': 6, '@md': 10 }}
              >
                {items.map((item, index) => (
                  <Item
                    key={`mobile_nav_${index}`}
                    item={{
                      href: item.href || '',
                      label: item.label,
                    }}
                    icon={item.href ? undefined : 'chevron-right'}
                    onClickNext={
                      !item.href ? () => onSelectNavItem(item) : undefined
                    }
                  />
                ))}
                <Item item={vehicleRedirect} />
              </Box>
            </Box>
            <MobileMenuNav
              Logo={Logo}
              vehicleType={vehicleType}
              onBack={clearSelectItem}
              onClose={onCloseHandler}
            />
          </m.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!!activeItem && (
          <m.aside
            className={MenuCss({
              css: {
                display: 'flex',
                flexDirection: 'column',
              },
            })}
            variants={animateVariants}
            initial="slideOut"
            animate="slideIn"
            exit="slideOut"
            transition={transition}
          >
            <MobileSubMenu
              Logo={Logo}
              activeItem={activeItem}
              vehicleType={vehicleType}
              onSetActiveItem={onSetActiveItem}
              onClose={onCloseHandler}
            />
          </m.aside>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
};
